import { useEffect, useState } from "react";

import { Presets } from "@similarweb/ui-components";

import { sanitizeUseCaseFilter } from "app/components/campaigns/shared/utils";
import { translate } from "app/filters/i18nFilter";
import type { IRTCurrentCampaignData } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { useKeywordsQuickFiltersQuery } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/hooks";
import type { RTRawParams } from "app/pages/digital-marketing/keywords-campaigns/reports/types";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { KeywordsReportTableFilter } from "./KeywordsReportTableFiltersConsts";

type Props = {
    currentCampaignConfiguration: IRTCurrentCampaignData;
    stateParams: RTRawParams;
    clickablePresets: boolean;
    handleCardSelected?: (id: KeywordsReportTableFilter) => void;
    isPendingUpdate?: boolean;
};

export const KeywordsReportKeywordsTablePresets = (props: Props) => {
    const { currentCampaignConfiguration, stateParams, handleCardSelected, isPendingUpdate = false } = props;
    const { useCaseFilter } = stateParams;
    const sanitizedUseCaseFilter = sanitizeUseCaseFilter(
        useCaseFilter,
        currentCampaignConfiguration.positionExcludedSerpFeatures,
    );
    const [selectedCardID, setSelectedCardID] = useState<KeywordsReportTableFilter | null>(
        sanitizedUseCaseFilter ?? null,
    );
    const { data: filtersData, isLoading: isLoadingData } = useKeywordsQuickFiltersQuery({
        currentCampaignConfiguration,
    });

    const isLoading = isLoadingData || isPendingUpdate;

    const handleSelect = (id: KeywordsReportTableFilter) => {
        allTrackers.dispatchTrackEvent("Filter Table", "click", `Presets Clicked/${id}`);
        if (handleCardSelected) {
            handleCardSelected(id);
        } else {
            if (id === sanitizedUseCaseFilter) {
                setSelectedCardID(null);
            } else {
                setSelectedCardID(id);
            }
        }
    };

    useEffect(() => {
        if (useCaseFilter && !sanitizedUseCaseFilter) {
            proNavigator.updateParams<RTRawParams>({ useCaseFilter: undefined });
            setSelectedCardID(null);
        }
    }, [useCaseFilter, sanitizedUseCaseFilter]);

    useEffect(() => {
        proNavigator.updateParams<RTRawParams>({ useCaseFilter: selectedCardID });
    }, [selectedCardID]);

    return (
        <Presets
            presetsData={filtersData}
            isLoading={isLoading}
            selectedPresetId={sanitizedUseCaseFilter}
            onSelectOrRemove={handleSelect}
            dataLabel={translate("rankTracker.keywords.presets.keywords")}
            title={translate("RT.Trend.presets.title")}
            enableCarousel={false}
        />
    );
};
